/**
 * chatFlow.js — Earn-E Onboarding Conversation State Machine
 * 
 * Drives the structured onboarding conversation. Each "step" in FLOW_STEPS
 * defines what the AI asks, how to validate the user's answer, and how to
 * extract structured data from free-text input.
 *
 * Usage:
 *   import { FLOW_STEPS, getStep, parseResponse, buildProfile } from './chatFlow';
 */

// ─── Step IDs ───────────────────────────────────────────────────────────────
export const STEPS = {
  GREETING: 'greeting',
  NUM_KIDS: 'num_kids',
  KID_NAMES: 'kid_names',
  CHALLENGES: 'challenges',
  CHORE_TYPES: 'chore_types',
  CHORE_SCHEDULE: 'chore_schedule',
  ALLOWANCE_SYSTEM: 'allowance_system',
  ALLOWANCE_BUDGET: 'allowance_budget',
  SCREEN_TIME: 'screen_time',
  GOALS_30: 'goals_30',
  GOALS_90: 'goals_90',
  EMAIL: 'email',
  GENERATING: 'generating',
  OFFER: 'offer',
  DONE: 'done',
};

// ─── Conversation Flow ───────────────────────────────────────────────────────
export const FLOW_STEPS = [
  {
    id: STEPS.GREETING,
    message: `👋 Hey there! I'm **Earn-E**, your AI family assistant.

I'm going to help you build a **custom chore & allowance system** for your family — one that actually works for your kids' ages and your family's lifestyle.

This takes about 5 minutes. Ready? Let's start!

**How many kids do you have?**`,
    quickReplies: ['1', '2', '3', '4', '5+'],
    inputType: 'number',
    field: null, // greeting only
    next: STEPS.NUM_KIDS,
  },
  {
    id: STEPS.NUM_KIDS,
    message: null, // message is dynamic based on previous answer
    inputType: 'number',
    field: 'family_structure.num_kids',
    validate: (input) => {
      const n = parseInt(input.replace(/\D/g, ''));
      return !isNaN(n) && n > 0 && n <= 15;
    },
    parse: (input) => {
      const n = parseInt(input.replace(/\D/g, ''));
      return isNaN(n) ? 1 : Math.min(n, 15);
    },
    buildNextMessage: (profile) => {
      const n = profile.family_structure?.num_kids || 1;
      return `Great! ${n} ${n === 1 ? 'kid' : 'kids'} — love it! 🎉

**What are their names and ages?**

You can type something like:
*"Emma 8, Liam 12, Noah 5"*
or just
*"Emma 8"* if you have one.`;
    },
    next: STEPS.KID_NAMES,
  },
  {
    id: STEPS.KID_NAMES,
    message: null,
    inputType: 'text',
    field: 'kids',
    validate: (input) => input.trim().length > 0,
    parse: (input) => parseKids(input),
    // This is the ARRIVAL message for KID_NAMES (shown after NUM_KIDS processed)
    // At this point profile.kids is NOT yet set — so we ask for names
    buildNextMessage: (profile) => {
      const n = profile.family_structure?.num_kids || 1;
      return `Awesome, ${n} ${n === 1 ? 'kid' : 'kids'}! 🎉

**What are their names and ages?**

Type something like: *"Emma 8, Liam 12"*`;
    },
    quickReplies: [],
    next: STEPS.CHALLENGES,
  },
  {
    id: STEPS.CHALLENGES,
    message: null,
    inputType: 'text',
    field: 'challenges',
    validate: (input) => input.trim().length > 0,
    parse: (input) => parseChallenges(input),
    // This is the ARRIVAL message for CHALLENGES (shown after KID_NAMES processed)
    // At this point profile.kids IS populated — safe to use names
    buildNextMessage: (profile) => {
      const names = (profile.kids || []).map(k => k.name).join(', ');
      const greeting = names ? `Nice to meet ${names}! 👋\n\n` : '';
      return `${greeting}Got it — you're not alone, those are super common! 💪

**What are your biggest challenges with chores right now?**

Select all that apply, or just describe it in your own words:`;
    },
    quickReplies: [
      '🔁 Constant nagging',
      '😤 Kids forget',
      '💰 Allowance confusion',
      '📱 Screen time battles',
      '😫 Nothing works',
      'Other...',
    ],
    allowMultiple: true,
    next: STEPS.CHORE_TYPES,
  },
  {
    id: STEPS.CHORE_TYPES,
    message: null,
    inputType: 'text',
    field: 'chore_preferences.types',
    validate: (input) => input.trim().length > 0,
    parse: (input) => input.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
    buildNextMessage: () =>
      `Perfect! 📋

**When do you want chores done?**`,
    quickReplies: [
      '🌅 Morning (before school)',
      '🌆 After school',
      '🌙 Evening (before bed)',
      '📅 Weekends only',
      'Mix of all',
    ],
    allowMultiple: true,
    next: STEPS.CHORE_SCHEDULE,
  },
  {
    id: STEPS.CHORE_SCHEDULE,
    message: null,
    inputType: 'text',
    field: 'chore_preferences.schedule',
    validate: (input) => input.trim().length > 0,
    parse: (input) => input.trim(),
    buildNextMessage: () =>
      `Great schedule! ⏰

**Now let's talk allowance. What system sounds right for you?**`,
    quickReplies: [
      '💼 Commission (earn per chore)',
      '📅 Weekly flat amount',
      '🎯 Bonus for extras',
      '🚫 No allowance (just responsibility)',
    ],
    next: STEPS.ALLOWANCE_SYSTEM,
  },
  {
    id: STEPS.ALLOWANCE_SYSTEM,
    message: null,
    inputType: 'text',
    field: 'allowance_preferences.system',
    validate: (input) => input.trim().length > 0,
    parse: (input) => {
      const lower = input.toLowerCase();
      // Handle dollar amount presets (e.g. "$40/week") — extract budget and default system to 'flat'
      if (/\$[\d]/.test(input) || /[\d]+\s*(\/week|total|per kid)/i.test(input)) {
        // Store budget in a side-channel via the raw value
        return 'flat';
      }
      if (lower.includes('commission') || lower.includes('per chore') || lower.includes('earn'))
        return 'commission';
      if (lower.includes('flat') || lower.includes('weekly') || lower.includes('fixed'))
        return 'flat';
      if (lower.includes('bonus') || lower.includes('extra'))
        return 'bonus';
      if (lower.includes('no') || lower.includes('none') || lower.includes('responsibility'))
        return 'none';
      return input.trim();
    },
    buildNextMessage: (profile) => {
      const system = profile.allowance_preferences?.system;
      if (system === 'none') {
        return `Got it — focusing on responsibility, not money. That's a great approach! 💪

**What's your weekly budget for allowance (if you added it later)?**

Or type *"skip"* if this doesn't apply.`;
      }
      return `Smart choice! 💡

**What's your weekly allowance budget?** (total for all kids)

Example: *"$15/week"* or *"$5 per kid"* — whatever works for you.`;
    },
    next: STEPS.ALLOWANCE_BUDGET,
  },
  {
    id: STEPS.ALLOWANCE_BUDGET,
    message: null,
    inputType: 'text',
    field: 'allowance_preferences.budget_weekly',
    validate: (input) => input.trim().length > 0,
    parse: (input) => {
      if (input.toLowerCase() === 'skip') return 0;
      // Handle "80 total, 20 per kid" → extract the total (first number)
      const totalMatch = input.match(/\$?([\d]+)\s*(total|\/week|per week|weekly|\/wk)/i);
      if (totalMatch) return parseFloat(totalMatch[1]);
      // Plain number or $XX format
      const match = input.match(/\$?([\d.]+)/);
      return match ? parseFloat(match[1]) : 0;
    },
    buildNextMessage: () =>
      `Nice! 💰

**What are your screen time rules?** (or what do you *want* them to be?)`,
    quickReplies: [
      '📱 30 min/day weekdays',
      '📱 1 hour/day weekdays',
      '📱 2 hours/day weekdays',
      '🎮 Only after chores',
      '🚫 No screens weekdays',
      'Weekends only',
      '✏️ Other (tell us your rules)',
    ],
    next: STEPS.SCREEN_TIME,
  },
  {
    id: STEPS.SCREEN_TIME,
    message: null,
    inputType: 'text',
    field: 'screen_time_rules',
    validate: (input) => input.trim().length > 0,
    parse: (input) => parseScreenTime(input),
    buildNextMessage: () =>
      `Smart rules! 📵

Almost done — just 2 quick questions about your goals.

**What's your #1 goal for the next 30 days?**`,
    quickReplies: [
      '🚫 Stop nagging constantly',
      '✅ Kids do chores independently',
      '💸 Teach money responsibility',
      '🤝 More family teamwork',
      '⚡ Less chaos in the house',
    ],
    next: STEPS.GOALS_30,
  },
  {
    id: STEPS.GOALS_30,
    message: null,
    inputType: 'text',
    field: 'goals.30_day',
    validate: (input) => input.trim().length > 0,
    parse: (input) => input.trim(),
    buildNextMessage: () =>
      `Love it! 🎯

**And in 90 days, what does success look like for your family?**`,
    quickReplies: [
      '🏆 Kids manage chores without reminders',
      '💰 Kids understand saving & spending',
      '😌 Peaceful, organized home',
      '📱 Screen time under control',
      '🌟 Kids have real responsibility',
    ],
    next: STEPS.GOALS_90,
  },
  {
    id: STEPS.GOALS_90,
    message: null,
    inputType: 'text',
    field: 'goals.90_day',
    validate: (input) => input.trim().length > 0,
    parse: (input) => input.trim(),
    buildNextMessage: () =>
      `Amazing goals! 🌟 You've clearly thought about this.

**One last thing — where should I send your custom chore chart, allowance plan, and recommendations?**

📧 Enter your email address:`,
    next: STEPS.EMAIL,
  },
  {
    id: STEPS.EMAIL,
    message: null,
    inputType: 'email',
    field: 'email',
    validate: (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim()),
    parse: (input) => input.trim().toLowerCase(),
    buildNextMessage: () => null, // handled by GENERATING state
    next: STEPS.GENERATING,
  },
  {
    id: STEPS.GENERATING,
    message: `✨ **Building your custom family plan...**

Give me just a moment while I:
- 📋 Create age-appropriate chore charts for each kid
- 💰 Calculate allowance recommendations  
- 📱 Design your screen time schedule
- 📚 Find the perfect blog posts for your challenges

This will be in your inbox shortly! 🎉`,
    next: STEPS.OFFER,
  },
  {
    id: STEPS.OFFER,
    message: null, // built dynamically with family name
    next: STEPS.DONE,
  },
];

// ─── Helper: getStep ─────────────────────────────────────────────────────────
export function getStep(stepId) {
  return FLOW_STEPS.find(s => s.id === stepId) || null;
}

export function getNextStepId(currentStepId, profile) {
  const step = getStep(currentStepId);
  if (!step) return null;
  return step.next || null;
}

// ─── Helper: Apply parsed value into profile ──────────────────────────────────
export function applyToProfile(profile, field, value) {
  if (!field) return profile;
  const parts = field.split('.');
  const updated = JSON.parse(JSON.stringify(profile)); // deep clone
  let obj = updated;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!obj[parts[i]] || typeof obj[parts[i]] !== 'object') {
      obj[parts[i]] = {};
    }
    obj = obj[parts[i]];
  }
  obj[parts[parts.length - 1]] = value;
  return updated;
}

// ─── Helper: Build offer message ─────────────────────────────────────────────
export function buildOfferMessage(profile) {
  const kidNames = (profile.kids || []).map(k => k.name).join(', ');
  const firstName = profile.kids?.[0]?.name || 'your family';
  return `🎁 Your custom plan for ${kidNames || 'your family'} is ready!

I've emailed you:
- ✅ Age-appropriate chore charts for each kid
- ✅ Allowance recommendations & weekly plan
- ✅ Screen time schedule that fits your rules
- ✅ Blog posts tailored to your specific challenges

---

🚀 **Want me to set all of this up automatically in Earn-E?**

Sign up now and get **30 days FREE** (instead of 14) — and your dashboard will be **pre-populated** with:
- ${firstName}'s profile and chores already added
- Allowance amounts already configured
- Schedule ready to go
- Time to first completed chore: **under 5 minutes**

**Ready to get started?**`;
}

// ─── Parsers ─────────────────────────────────────────────────────────────────

function parseKids(input) {
  // Try patterns like "Emma 8, Liam 12" or "Emma (8), Liam (12)" or "Emma age 8"
  const kids = [];
  
  // Split by comma or "and"
  const segments = input.split(/,\s*|\s+and\s+/i);
  
  for (const seg of segments) {
    const trimmed = seg.trim();
    if (!trimmed) continue;
    
    // Match: Name Age or Name (Age) or Name, age Age
    // eslint-disable-next-line no-useless-escape
    const match = trimmed.match(/^([A-Za-z\-']+)\s*[(\s,]*(\d{1,2})[)\s]*/);
    if (match) {
      const name = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
      const age = parseInt(match[2]);
      kids.push({ name, age, grade: ageToGrade(age) });
    } else {
      // Just a name without age
      const nameMatch = trimmed.match(/^([A-Za-z\-']+)/);
      if (nameMatch) {
        const name = nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1).toLowerCase();
        kids.push({ name, age: null, grade: null });
      }
    }
  }
  
  // If nothing parsed, treat whole input as one kid name
  if (kids.length === 0 && input.trim()) {
    kids.push({ name: input.trim(), age: null, grade: null });
  }
  
  return kids;
}

function ageToGrade(age) {
  if (age < 5) return 'Pre-K';
  if (age === 5) return 'K';
  if (age >= 6 && age <= 17) return `${age - 5}th`;
  return 'Adult';
}

function parseChallenges(input) {
  const lower = input.toLowerCase();
  const pain_points = [];
  
  if (lower.includes('nag')) pain_points.push('nagging');
  if (lower.includes('forget')) pain_points.push('forgetting');
  if (lower.includes('allowance') || lower.includes('money')) pain_points.push('allowance_confusion');
  if (lower.includes('screen') || lower.includes('phone') || lower.includes('device')) pain_points.push('screen_time');
  if (lower.includes('nothing work') || lower.includes('nothing works')) pain_points.push('tried_everything');
  if (lower.includes('consistent')) pain_points.push('consistency');
  
  if (pain_points.length === 0) {
    // Use the raw text
    pain_points.push(input.trim().substring(0, 100));
  }
  
  return {
    pain_points,
    tried_before: [],
    what_worked: '',
    raw: input.trim(),
  };
}

function parseScreenTime(input) {
  const lower = input.toLowerCase();
  
  // Extract minutes from common phrases
  let weekday = 60; // default
  let weekend = 120;
  
  if (lower.includes('30 min') || lower.includes('half hour')) weekday = 30;
  else if (lower.includes('1 hour') || lower.includes('one hour')) weekday = 60;
  else if (lower.includes('2 hour') || lower.includes('two hour')) weekday = 120;
  else if (lower.includes('no screen') || lower.includes('no device')) weekday = 0;
  
  // After chores = conditional
  const rewards = [];
  if (lower.includes('after chore') || lower.includes('earn')) {
    rewards.push('Screens only after chores are done');
  }
  
  if (lower.includes('weekend')) weekend = 180;

  // Check if "Other" was triggered — store raw custom rules text as-is
  const isCustom = !['30 min', '1 hour', '2 hour', 'only after chores', 'no screens', 'weekends only'].some(
    phrase => lower.includes(phrase.toLowerCase())
  );
  
  return {
    weekday_minutes: weekday,
    weekend_minutes: weekend,
    rules: input.trim(),
    custom_rules: isCustom ? input.trim() : null,
    rewards,
  };
}

// ─── Build Full Profile ───────────────────────────────────────────────────────
export function buildInitialProfile() {
  return {
    email: '',
    phone: '',
    family_structure: { num_kids: 0, parents: 1, caregivers: [] },
    kids: [],
    challenges: { pain_points: [], tried_before: [], what_worked: '' },
    chore_preferences: { types: [], schedule: '', non_negotiables: [] },
    allowance_preferences: { system: 'commission', budget_weekly: 0, savings_percent: 20 },
    screen_time_rules: { weekday_minutes: 60, weekend_minutes: 120, rewards: [] },
    goals: { '30_day': '', '90_day': '' },
    signup_code: null,
    signed_up_at: null,
    created_at: new Date(),
  };
}

export function isProfileComplete(profile) {
  return !!(
    profile.email &&
    profile.kids?.length > 0 &&
    profile.goals?.['30_day']
  );
}

// ─── Chore Suggestions by Age ─────────────────────────────────────────────────
export function getAgeAppropriateChores(age) {
  if (age === null || age === undefined) return defaultChores();
  if (age <= 4) return ['Put toys away', 'Help feed pets', 'Pick up clothes'];
  if (age <= 6) return ['Make bed (simple)', 'Put clothes in hamper', 'Set table', 'Water plants', 'Feed pets'];
  if (age <= 9) return ['Make bed', 'Clear dishes', 'Wipe counters', 'Take out small trash', 'Feed pets', 'Vacuum room'];
  if (age <= 12) return ['Wash dishes', 'Do laundry (with help)', 'Mow lawn (supervised)', 'Cook simple meals', 'Vacuum whole house', 'Take out trash'];
  return ['Full laundry', 'Cook dinner', 'Deep clean', 'Mow lawn', 'Grocery run', 'Car wash', 'Babysit siblings'];
}

function defaultChores() {
  return ['Put toys away', 'Help with dishes', 'Tidy room'];
}

// ─── Allowance Recommendations by Age ────────────────────────────────────────
export function getAllowanceByAge(age, system = 'commission') {
  if (age === null || age === undefined) return { weekly: 5, perChore: 0.5 };
  
  // Common rule: $0.50-$1 per year of age per week
  const weeklyBase = Math.round(age * 0.75);
  const perChore = Math.round(weeklyBase / 5 * 100) / 100;
  
  return {
    weekly: weeklyBase,
    perChore,
    monthly: weeklyBase * 4,
    savingsGoal: Math.round(weeklyBase * 0.2 * 100) / 100, // 20% savings
    system,
  };
}
